import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { updateWorkspace } from '@/services/workspace';
import { errorHandler } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
export function BrandingCard({ workspace }) {
    const { t } = useTranslation();
    const isOwner = workspace.role === 'OWNER';
    const canRemove = workspace.canRemoveWatermark;
    const [showBranding, setShowBranding] = React.useState(!workspace.hideWatermark);
    const { mutate, isPending } = useMutation({
        mutationFn: updateWorkspace,
        onSuccess: (data) => {
            toast.success(data.message);
        },
        onError: (err) => {
            setShowBranding(!workspace.hideWatermark);
            errorHandler(err);
        },
    });
    const handleToggle = React.useCallback((checked) => {
        setShowBranding(checked);
        const formData = new FormData();
        formData.append('name', workspace.name);
        formData.append('telegramChatId', workspace.telegramChatId ?? '');
        formData.append('hideWatermark', checked ? '0' : '1');
        mutate({ workspaceId: workspace.publicId, data: formData });
    }, [mutate, workspace.name, workspace.publicId, workspace.telegramChatId]);
    const disabled = !isOwner || !canRemove || isPending;
    return (<Card>
            <CardHeader>
                <CardTitle className="text-base md:text-lg">
                    {t('workspaces.branding.title', { ns: 'glossary', defaultValue: 'Branding' })}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                        <Label className="text-sm font-medium">
                            {t('workspaces.branding.show_label', {
            ns: 'glossary',
            defaultValue: 'Show Adora branding on public pages',
        })}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                            {t('workspaces.branding.show_help', {
            ns: 'glossary',
            defaultValue: 'Controls the "Powered by Adora" footer that appears under every smart-link landing page.',
        })}
                        </p>
                    </div>
                    <Switch checked={canRemove ? showBranding : true} onCheckedChange={handleToggle} disabled={disabled} aria-label={t('workspaces.branding.show_label', {
            ns: 'glossary',
            defaultValue: 'Show Adora branding on public pages',
        })}/>
                </div>

                {!canRemove && (<div className="flex items-start gap-2 rounded-md border border-dashed border-primary/30 bg-primary/5 p-3">
                        <Sparkles className="h-4 w-4 mt-0.5 text-primary shrink-0"/>
                        <div className="space-y-1 text-xs">
                            <p className="font-medium">
                                {t('workspaces.branding.upgrade_title', {
                ns: 'glossary',
                defaultValue: 'Available on Solo and Team',
            })}
                            </p>
                            <p className="text-muted-foreground">
                                {t('workspaces.branding.upgrade_help', {
                ns: 'glossary',
                defaultValue: 'Upgrade to remove the "Powered by Adora" footer from your public pages.',
            })}
                            </p>
                            {isOwner && (<Link to="/console/workspace/billing" className="inline-block text-primary hover:underline font-medium">
                                    {t('workspaces.branding.upgrade_cta', {
                    ns: 'glossary',
                    defaultValue: 'View plans →',
                })}
                                </Link>)}
                        </div>
                    </div>)}
            </CardContent>
        </Card>);
}
